import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Archive,
  Boxes,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Info,
  MoreHorizontal,
  Pencil,
  Plus,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { iqServiceStatusBadgeVariant } from '@/components/features/merchants/iq-service-inventory/iq-service-inventory-status';
import { IqServiceInventoryArchiveDialog } from '@/components/features/merchants/iq-service-inventory/iq-service-inventory-archive-dialog';
import { PlatformIqServiceUpsertSheet } from '@/components/features/merchants/iq-service-inventory/platform-iq-service-upsert-sheet';
import { PlatformIqInventoryFilterBar } from '@/components/features/merchants/iq-service-inventory/platform-iq-inventory-filter-bar';
import {
  usePlatformIqServiceInventory,
  useArchiveIqServiceInventoryItem,
} from '@/hooks/use-iq-service-inventory';
import { useMerchantMap } from '@/hooks/use-merchant-map';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import type {
  PlatformIqInventoryFilters,
  PlatformIqInventoryRow,
  PlatformIqInventorySort,
  PlatformIqInventorySortKey,
} from '@/types/iq-service-inventory';

/**
 * Plattformweite Cross-Merchant-Uebersicht der IQ-Service-Kennungen (Spec 347 §8) mit
 * Overview-CRUD (Spec 356): registrieren/bearbeiten/archivieren direkt auf der Tabelle.
 *
 * Platform-only (Route-Gate IQ_SERVICE_INVENTORY_OVERVIEW). Das View-Gate ist BREITER
 * als das Write-Gate: `platform_ops` sieht die Tabelle, schreiben duerfen nur
 * `platform_admin`/`platform_it` (IQ_SERVICE_INVENTORY_CRUD). Die Schreib-Affordanzen
 * sind daher in-Komponente auf `canCrud` gegated.
 *
 * Degradation:
 *   - usageUnavailable → Banner „nicht ermittelbar", Zaehler stehen auf 0 (nicht belastbar)
 *   - usageTruncated   → Banner „ggf. unvollstaendig"
 *   - failedMerchants  → eigene Markierung „Inventar nicht ermittelbar" (NICHT als 0-Zeilen)
 */

/** Amber/destruktiver Warn-Banner (Haus-Stil aus theme.tsx / mno-ip-ranges). */
function DegradationBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export function PlatformIqInventoryPage() {
  const { t } = useTranslation('iq-service-inventory');
  const { hasRole } = useAuth();
  const canCrud = hasRole(ROLE_GROUPS.IQ_SERVICE_INVENTORY_CRUD);
  const { data, isLoading, error, refetch } = usePlatformIqServiceInventory();
  const { resolveMerchantName } = useMerchantMap();

  // Overview-CRUD host state (Spec 356). The merchant is the path parameter of the
  // reused per-merchant routes: picked in the register drawer, pinned from the row for
  // edit/archive. Cleared on close so the next register starts clean.
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<PlatformIqInventoryRow | null>(null);
  const [archiveRow, setArchiveRow] = useState<PlatformIqInventoryRow | null>(null);

  // Archive runs over the reused per-merchant DELETE route; the hook (Weg A) refreshes
  // the overview aggregate on success. Idempotent, no token — just the row's key.
  const archiveMutation = useArchiveIqServiceInventoryItem(archiveRow?.merchantId ?? '');
  const handleArchiveConfirm = async () => {
    if (!archiveRow) return;
    try {
      await archiveMutation.mutateAsync(archiveRow.iqServiceKey);
      toast.success(t('archive.success'));
      setArchiveRow(null);
    } catch {
      toast.error(t('archive.error'));
    }
  };

  // Spec 384 §2: client-side filter + sort state (the aggregate endpoint takes no params).
  const [filters, setFilters] = useState<PlatformIqInventoryFilters>({});
  const [sort, setSort] = useState<PlatformIqInventorySort>({ key: 'merchant', dir: 'asc' });
  const updateFilter = (patch: Partial<PlatformIqInventoryFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const resetFilters = () => setFilters({});
  const hasActiveFilters = Boolean(
    filters.merchantId ||
      filters.status ||
      filters.flowType ||
      filters.bookingType ||
      filters.text?.trim(),
  );
  const toggleSort = (key: PlatformIqInventorySortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  const sortIcon = (key: PlatformIqInventorySortKey) =>
    sort.key === key ? (
      sort.dir === 'asc' ? (
        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      )
    ) : (
      <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" aria-hidden="true" />
    );
  const ariaSort = (key: PlatformIqInventorySortKey): 'ascending' | 'descending' | 'none' =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  // Spec 384 §3: the routing-usage column's Flow/Booking labels degrade gracefully — a known
  // enum renders its i18n label, an unknown (Facade-added) value renders verbatim (never a raw
  // `flowType.XYZ` key), and an absent annotation falls back to "—".
  const annotationLabel = (ns: 'flowType' | 'bookingType', value?: string) =>
    value ? t(`${ns}.${value}`, { defaultValue: value }) : t('detail.notSet');

  const allRows = data?.rows ?? [];

  // Filter (AND across facets) then sort — both over the already-loaded rows.
  const filtered = useMemo(
    () =>
      allRows.filter((row) => {
        if (filters.merchantId && row.merchantId !== filters.merchantId) return false;
        if (filters.status && row.status !== filters.status) return false;
        if (filters.flowType && row.flowType !== filters.flowType) return false;
        if (filters.bookingType && row.bookingType !== filters.bookingType) return false;
        const q = filters.text?.trim().toLowerCase();
        if (q) {
          const hay = `${row.iqServiceKey} ${row.displayName ?? ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      }),
    [allRows, filters],
  );

  const view = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      let cmp: number;
      if (sort.key === 'merchant') {
        cmp = resolveMerchantName(a.merchantId).localeCompare(resolveMerchantName(b.merchantId));
      } else if (sort.key === 'status') {
        cmp = a.status.localeCompare(b.status);
      } else {
        // Routing-usage: numeric on the underlying count (stable even when the cell renders
        // "not determinable" under degradation).
        cmp = a.fallbackUsageCount - b.fallbackUsageCount;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filtered, sort, resolveMerchantName]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title={t('overview.title')} description={t('overview.subtitle')} />
        <Card>
          <CardContent className="p-4">
            <TableSkeleton rows={5} cols={7} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader title={t('overview.title')} description={t('overview.subtitle')} />
        <ApiErrorDisplay error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  const failedMerchants = data?.failedMerchants ?? [];

  // Spec 384 §2: the filtered-empty state shows NO register CTA — when rows exist but the
  // filter hides them all, clearing the filter (not registering) is the recovery path. The
  // registered-empty state (allRows empty) keeps the CTA as the only way to add the first entry.
  const isFilteredEmpty = allRows.length > 0 && view.length === 0;

  // Register is the only escape from the registered-empty state, so it lives outside the
  // conditional branches below — but is deliberately absent during the isLoading/error
  // early-returns and the filtered-empty state. canCrud-gated.
  const registerAction = canCrud && !isFilteredEmpty ? (
    <Button size="sm" onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" />
      {t('register')}
    </Button>
  ) : undefined;

  return (
    <div>
      <PageHeader
        title={t('overview.title')}
        description={t('overview.subtitle')}
        actions={registerAction}
      />

      {data?.usageUnavailable && (
        <DegradationBanner>{t('overview.banner.unavailable')}</DegradationBanner>
      )}
      {data?.usageTruncated && (
        <DegradationBanner>{t('overview.banner.truncated')}</DegradationBanner>
      )}

      {allRows.length === 0 && failedMerchants.length === 0 ? (
        // True empty: nothing registered AND nothing failed.
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            <Boxes className="mx-auto mb-3 h-6 w-6" aria-hidden="true" />
            {t('overview.empty')}
          </CardContent>
        </Card>
      ) : allRows.length === 0 ? (
        // All merchants failed (rows empty BUT failedMerchants present): do NOT show
        // the "nothing registered" empty state — that misreads a retrieval failure as
        // "no data". The failedMerchants marker below conveys the real situation.
        null
      ) : (
        // Rows exist → the filter bar narrows them; the filtered-empty state below is a
        // DISTINCT third terminal state (no register CTA) and never collapses into the
        // registered-empty/all-failed branches above (which only fire when allRows is empty).
        <>
          <PlatformIqInventoryFilterBar
            filters={filters}
            rows={allRows}
            hasActiveFilters={hasActiveFilters}
            onChange={updateFilter}
            onReset={resetFilters}
          />
          <p className="mb-2 text-sm text-muted-foreground" aria-live="polite">
            {t('overview.filter.resultCount', { count: view.length })}
          </p>
          {view.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                {t('overview.filter.filteredEmpty')}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="px-0 py-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead aria-sort={ariaSort('merchant')}>
                        <button
                          type="button"
                          onClick={() => toggleSort('merchant')}
                          className="inline-flex items-center gap-1 font-medium"
                        >
                          {t('overview.columns.merchant')}
                          {sortIcon('merchant')}
                        </button>
                      </TableHead>
                      <TableHead>{t('overview.columns.iqServiceKey')}</TableHead>
                      {/* Spec 384 §1: the row's own annotations (not sortable in v1). */}
                      <TableHead>{t('overview.columns.name')}</TableHead>
                      <TableHead>{t('overview.columns.flowType')}</TableHead>
                      <TableHead>{t('overview.columns.bookingType')}</TableHead>
                      <TableHead aria-sort={ariaSort('status')}>
                        <button
                          type="button"
                          onClick={() => toggleSort('status')}
                          className="inline-flex items-center gap-1 font-medium"
                        >
                          {t('overview.columns.status')}
                          {sortIcon('status')}
                        </button>
                      </TableHead>
                      <TableHead className="text-right" aria-sort={ariaSort('usage')}>
                        {/* Spec 384 §2+§3: sortable (numeric on fallbackUsageCount) AND the
                            de-jargoned label's tooltip carries the dimension note. The single
                            button does double duty: click → sort, hover/focus → note. */}
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => toggleSort('usage')}
                                className="ml-auto inline-flex items-center gap-1 text-sm font-medium underline decoration-dotted underline-offset-2"
                              >
                                {t('overview.columns.usedByGateway')}
                                {sortIcon('usage')}
                                <Info
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  aria-hidden="true"
                                />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left font-normal">
                              {t('overview.gatewayDimensionNote')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableHead>
                      {canCrud && (
                        <TableHead className="w-[1%] text-right">
                          <span className="sr-only">{t('list.rowActions')}</span>
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {view.map((row) => (
                  <TableRow key={`${row.merchantId}::${row.iqServiceKey}`}>
                    <TableCell className="font-medium">
                      {resolveMerchantName(row.merchantId)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.iqServiceKey}</TableCell>
                    {/* Spec 384 §1: own annotations; absent → "—", unknown enum → raw value. */}
                    <TableCell>{row.displayName || t('detail.notSet')}</TableCell>
                    <TableCell>{annotationLabel('flowType', row.flowType)}</TableCell>
                    <TableCell>{annotationLabel('bookingType', row.bookingType)}</TableCell>
                    <TableCell>
                      <Badge variant={iqServiceStatusBadgeVariant(row.status)}>
                        {t(`status.${row.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {/* Spec 352 §6: klickbarer Zaehler → gefilterte /services-Sicht.
                          Nie ein falscher Wert: usageUnavailable ODER usageTruncated →
                          "nicht ermittelbar" (der Zaehler ist dann unzuverlaessig),
                          0 → "ungenutzt" (beide Klartext); nur ein belastbares N>0 wird
                          verlinkt. `&merchant=` mitgeben (Platform kennt den Merchant). */}
                      {data?.usageUnavailable || data?.usageTruncated
                        ? t('usage.usageUnknown')
                        : row.fallbackUsageCount === 0
                          ? t('usage.notUsed')
                          : (
                              <Link
                                to={`/services?gw=${encodeURIComponent(row.iqServiceKey)}&merchant=${encodeURIComponent(row.merchantId)}`}
                                className="hover:text-primary hover:underline"
                              >
                                {t('usage.inEntries', { count: row.fallbackUsageCount })}
                              </Link>
                            )}
                    </TableCell>
                    {canCrud && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 coarse:min-h-11 coarse:min-w-11">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">{t('list.rowActions')}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditRow(row)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('list.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setArchiveRow(row)}>
                              <Archive className="mr-2 h-4 w-4" />
                              {t('list.archive')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {failedMerchants.length > 0 && (
        <div
          role="alert"
          className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('overview.failedMerchants.heading')}
          </p>
          <p className="mt-1">{t('overview.failedMerchants.description')}</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {failedMerchants.map((id) => (
              <li key={id}>
                <Badge variant="outline" className="font-mono">
                  {resolveMerchantName(id)}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Spec 356 — Overview-CRUD drawers/dialog. Phase 1 mounts them as shells (always
          mounted, controlled by open state); the register picker, edit prefetch, archive
          confirm + mutation wiring are filled in Phases 2-4. */}
      <PlatformIqServiceUpsertSheet
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setCreateOpen(false);
        }}
        mode="create"
      />
      <PlatformIqServiceUpsertSheet
        open={editRow !== null}
        onOpenChange={(open) => {
          if (!open) setEditRow(null);
        }}
        mode="edit"
        row={editRow}
        merchantName={editRow ? resolveMerchantName(editRow.merchantId) : undefined}
      />
      <IqServiceInventoryArchiveDialog
        open={archiveRow !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveRow(null);
        }}
        item={archiveRow ? { iqServiceKey: archiveRow.iqServiceKey } : null}
        merchantName={archiveRow ? resolveMerchantName(archiveRow.merchantId) : undefined}
        onConfirm={handleArchiveConfirm}
        isPending={archiveMutation.isPending}
      />
    </div>
  );
}
